/**
 * Shift review — the screen the portal exists for.
 *
 * Filters, the full flag set, and a CSV export that excludes anything needing a human
 * decision unless the user explicitly opts in.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Badge, Card, ErrorNotice, PageHeader, Spinner, StatusDot } from '@/components/ui';
import { useI18n, type I18n } from '@/i18n';
import { MobileList } from '@/components/MobileList';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { api, type Shift, type User } from '@/lib/api';
import { useTableState, type SortOption } from '@/lib/table';
import { dateShort, decimalHours, distance, duration, fromMilan, fullName, isoDate, time } from '@/lib/format';
import { useLabel, type LabelFn } from '@/lib/useLabel';
import { Combobox } from '@/components/Combobox';
import { personOptions } from '@/features/access/people';

function startOfMonth(): string {
  const d = new Date();
  d.setDate(1);
  return isoDate(d);
}

/**
 * Timesheet export.
 *
 * Auto-closed and disputed shifts are EXCLUDED by default and must be opted into.
 * An auto-closed shift is a guess the system made because someone forgot to clock out;
 * paying it out unreviewed is exactly the mistake this whole flag system exists to
 * prevent (docs/06).
 */
function toCsv(
  shifts: Shift[],
  userById: Map<string, User>,
  includeUnreviewed: boolean,
  // Not hook calls: this runs from a click handler, outside React's render.
  L: LabelFn,
  t: I18n['t'],
  headings: string[],
): string {
  const rows = shifts.filter(
    (s) => includeUnreviewed || (s.status !== 'auto_closed' && s.status !== 'disputed'),
  );

  const header = headings;

  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;

  const lines = rows.map((s) => {
    const user = userById.get(s.userId);
    return [
      user ? fullName(user) : s.userId,
      dateShort(s.clockInAt),
      s.jobReference ? `${s.jobReference} ${s.jobTitle ?? ''}`.trim() : '',
      s.siteName ?? '',
      time(s.clockInAt),
      s.clockOutAt ? time(s.clockOutAt) : '',
      String(s.breakMinutes),
      duration(s.durationMinutes),
      decimalHours(s.durationMinutes),
      L('shift', s.status).label,
      s.clockInWithinGeofence === true
        ? t('common.yes')
        : s.clockInWithinGeofence === false
          ? t('common.no')
          : t('common.notAvailable'),
      s.clockInDistanceM === null ? '' : String(Math.round(s.clockInDistanceM)),
      s.exceptionFlags.map((f) => L('flag', f).label).join('; '),
      s.overrideReason ?? '',
    ]
      .map(escape)
      .join(',');
  });

  // BOM so Excel opens it as UTF-8 rather than mangling every accented name. Written
  // as an escape, not the raw character, so it is visible to anyone reading this line.
  return `\ufeff${header.map(escape).join(',')}\n${lines.join('\n')}`;
}

/**
 * Flags worth showing next to a status badge.
 *
 * `auto_closed` is both a status and a flag, so rendering both produces the same
 * chip twice. The status badge already says it.
 */
function displayFlags(status: string, flags: string[]): string[] {
  return flags.filter((f) => !(f === 'auto_closed' && status === 'auto_closed'));
}

const SHIFT_SORTS: SortOption<Shift>[] = [
  { key: 'clockIn', labelKey: 'common.date', value: (s) => s.clockInAt, defaultDirection: 'desc' },
  { key: 'duration', labelKey: 'shifts.duration', value: (s) => s.durationMinutes, defaultDirection: 'desc' },
  { key: 'status', labelKey: 'common.status', value: (s) => s.status },
  { key: 'flags', labelKey: 'shifts.flags', value: (s) => s.exceptionFlags.length, defaultDirection: 'desc' },
];

export function ShiftsPage() {
  const { t, plural } = useI18n();
  const L = useLabel();
  const [from, setFrom] = useState(startOfMonth);
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [userId, setUserId] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // The exception queue as a list. What the dashboard's "to review" count points at,
  // and what payroll holds back — reachable from here rather than only one row at a
  // time from the dashboard.
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [includeUnreviewed, setIncludeUnreviewed] = useState(false);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });

  const filters = useMemo(
    () => ({
      from: from ? fromMilan(`${from}T00:00:00`) : undefined,
      to: to ? fromMilan(`${to}T23:59:59.999`) : undefined,
      userId: userId || undefined,
      flagged: flaggedOnly || undefined,
      needsReview: needsReviewOnly || undefined,
      limit: 200,
    }),
    [from, to, userId, flaggedOnly, needsReviewOnly],
  );

  const shifts = useQuery({
    queryKey: ['shifts', filters],
    queryFn: () => api.shifts(filters),
  });

  const userById = useMemo(
    () => new Map((users.data?.users ?? []).map((u) => [u.id, u])),
    [users.data],
  );

  // Memoised so the empty-array fallback isn't a fresh identity on every render, which
  // would make every downstream useMemo recompute for nothing.
  const allRows = useMemo(() => shifts.data?.shifts ?? [], [shifts.data]);

  const table = useTableState<Shift>({
    rows: allRows,
    search: (s) => {
      const user = userById.get(s.userId);
      return [
        user ? fullName(user) : null,
        s.jobReference,
        s.jobTitle,
        s.siteName,
        s.siteAddress,
        L('shift', s.status).label,
        s.overrideReason,
        ...s.exceptionFlags.map((f) => L('flag', f).label),
      ];
    },
    sorts: SHIFT_SORTS,
    defaultSortKey: 'clockIn',
    storageKey: 'shifts',
  });
  const rows = table.rows;

  const totals = useMemo(() => {
    const reviewable = rows.filter((s) => s.status !== 'auto_closed' && s.status !== 'disputed');
    return {
      minutes: reviewable.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0),
      excluded: rows.length - reviewable.length,
    };
  }, [rows]);

  const download = () => {
    const csv = toCsv(rows, userById, includeUnreviewed, L, t, [
      t('common.employee'),
      t('common.date'),
      t('common.job'),
      t('common.site'),
      t('shifts.csvIn'),
      t('shifts.csvOut'),
      t('shifts.csvBreak'),
      t('common.hours'),
      t('payroll.decimalHours'),
      t('common.status'),
      t('shifts.onSite'),
      t('shifts.csvDistance'),
      t('shifts.flags'),
      t('shifts.csvReason'),
    ]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `turni_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        title={t('shifts.title')}
        subtitle={t('shifts.subtitle')}
        actions={
          <button type="button" className="btn-secondary" onClick={download} disabled={rows.length === 0}>
            {t('shifts.exportCsv')}
          </button>
        }
      />

      <DataToolbar
        state={table}
        searchPlaceholder={t('shifts.searchPlaceholder')}
        countOne="shifts.countOne"
        countMany="shifts.countMany"
        extra={
          <>
            <input
              type="date"
              className="input w-auto"
              aria-label={t('common.from')}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <input
              type="date"
              className="input w-auto"
              aria-label={t('common.to')}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <Combobox
              className="w-64"
              ariaLabel={t('common.employee')}
              value={userId}
              onChange={setUserId}
              options={[{ value: '', label: t('common.allEmployees') }, ...personOptions(users.data?.users ?? [], L)]}
            />
            <label className="flex items-center gap-2 text-sm h-9 whitespace-nowrap">
              <input
                type="checkbox"
                checked={flaggedOnly}
                onChange={(e) => setFlaggedOnly(e.target.checked)}
              />
              {t('shifts.flaggedOnly')}
            </label>
            <label className="flex items-center gap-2 text-sm h-9 whitespace-nowrap">
              <input
                type="checkbox"
                checked={needsReviewOnly}
                onChange={(e) => setNeedsReviewOnly(e.target.checked)}
              />
              {t('shifts.onlyNeedsReview')}
            </label>
            <label
              className="flex items-center gap-2 text-sm h-9 whitespace-nowrap"
              title={t('shifts.includeUnreviewedHint')}
            >
              <input
                type="checkbox"
                checked={includeUnreviewed}
                onChange={(e) => setIncludeUnreviewed(e.target.checked)}
              />
              {t('shifts.includeUnreviewed')}
            </label>
          </>
        }
      />

      {totals.excluded > 0 && !includeUnreviewed ? (
        <Card className="mb-4 bg-warning-soft border-[var(--color-warning)]">
          <p className="text-sm">
            {plural(totals.excluded, 'shifts.excludedOne', 'shifts.excludedMany', {
              count: totals.excluded,
            })}
          </p>
        </Card>
      ) : null}

      {shifts.isLoading ? (
        <Spinner />
      ) : shifts.error ? (
        <ErrorNotice error={shifts.error} />
      ) : (
        <Card padded={false}>
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="text-sm text-muted">
              {plural(rows.length, 'shifts.countOne', 'shifts.countMany', { count: rows.length })}
            </span>
            <span className="text-sm">
              {t('shifts.reviewableTotal')}{' '}
              <strong className="tabular">{duration(totals.minutes)}</strong>
            </span>
          </div>

          {/* Phone: the same rows, tappable, with the columns that will not fit in
              the sheet behind them. See MobileList. */}
          <div className="lg:hidden p-2">
            <MobileList
              rows={rows}
              keyOf={(r) => r.id}
              title={(r) => {
                const u = userById.get(r.userId);
                return u ? fullName(u) : '\u2014';
              }}
              subtitle={(r) =>
                `${dateShort(r.clockInAt)} \u00b7 ${time(r.clockInAt)}${
                  r.clockOutAt ? ` \u2013 ${time(r.clockOutAt)}` : ' \u2013 \u2026'
                }`
              }
              trailing={(r) => duration(r.durationMinutes)}
              badges={(r) => (
                <>
                  {r.status !== 'closed' ? (
                    <Badge tone={L('shift', r.status).tone}>{L('shift', r.status).label}</Badge>
                  ) : null}
                  {displayFlags(r.status, r.exceptionFlags).map((f) => (
                    <Badge key={f} tone={L('flag', f).tone}>
                      {L('flag', f).label}
                    </Badge>
                  ))}
                </>
              )}
              fields={[
                {
                  label: t('shifts.jobAndSite'),
                  value: (r) =>
                    [r.jobReference ? `${r.jobReference} · ${r.jobTitle ?? ''}` : null, r.siteName]
                      .filter(Boolean)
                      .join(' — ') || '\u2014',
                },
                { label: t('shifts.duration'), value: (r) => duration(r.durationMinutes) },
                {
                  label: t('shifts.position'),
                  value: (r) =>
                    r.clockInWithinGeofence === true
                      ? t('shifts.onSite')
                      : r.clockInWithinGeofence === false
                        ? distance(r.clockInDistanceM)
                        : t('shifts.gpsUnavailable'),
                },
              ]}
              action={(r) => (
                <Link to={`/shifts/${r.id}`} className="btn-primary">
                  {t('common.details')}
                </Link>
              )}
            />
          </div>

          <div className="table-scroll max-lg:hidden">
            <table className="table-wide">
              <thead>
                <tr>
                  <th className="th">{t('common.employee')}</th>
                  <SortableTh state={table} sortKey="clockIn">
                    {t('common.date')}
                  </SortableTh>
                  <th className="th">{t('common.time')}</th>
                  <th className="th">{t('shifts.jobAndSite')}</th>
                  <SortableTh state={table} sortKey="duration">
                    {t('shifts.duration')}
                  </SortableTh>
                  <th className="th">{t('shifts.position')}</th>
                  <SortableTh state={table} sortKey="flags">
                    {t('shifts.flags')}
                  </SortableTh>
                  <th className="th" />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const user = userById.get(s.userId);
                  const status = L('shift', s.status);

                  return (
                    <tr key={s.id} className="hover:bg-surface-alt">
                      <td className="td font-semibold">{user ? fullName(user) : '—'}</td>
                      <td className="td tabular whitespace-nowrap">{dateShort(s.clockInAt)}</td>
                      <td className="td tabular whitespace-nowrap">
                        {time(s.clockInAt)}
                        {s.clockOutAt ? ` – ${time(s.clockOutAt)}` : ' – …'}
                      </td>
                      <td className="td max-w-64">
                        {s.jobId && s.jobReference ? (
                          <Link to={`/jobs/${s.jobId}`} className="block truncate font-medium hover:text-primary">
                            {s.jobReference} · {s.jobTitle}
                          </Link>
                        ) : (
                          <span className="text-subtle">{t('shifts.noJob')}</span>
                        )}
                        <span className="block text-xs text-muted truncate">{s.siteName ?? '—'}</span>
                      </td>
                      <td className="td tabular font-semibold">{duration(s.durationMinutes)}</td>
                      <td className="td whitespace-nowrap">
                        {s.clockInWithinGeofence === true ? (
                          <StatusDot tone="success">{t('shifts.onSite')}</StatusDot>
                        ) : s.clockInWithinGeofence === false ? (
                          <StatusDot tone="warning">{distance(s.clockInDistanceM)}</StatusDot>
                        ) : (
                          <StatusDot tone="neutral">{t('shifts.gpsUnavailable')}</StatusDot>
                        )}
                      </td>
                      <td className="td">
                        <div className="flex flex-wrap gap-1">
                          {s.status !== 'closed' ? <Badge tone={status.tone}>{status.label}</Badge> : null}
                          {displayFlags(s.status, s.exceptionFlags).map((f) => {
                            const spec = L('flag', f);
                            return (
                              <Badge key={f} tone={spec.tone}>
                                {spec.label}
                              </Badge>
                            );
                          })}
                        </div>
                      </td>
                      <td className="td text-end">
                        <Link to={`/shifts/${s.id}`} className="td-action text-primary hover:underline">
                          {t('common.details')}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {rows.length === 0 ? <NoResults onClear={table.clear} /> : null}
        </Card>
      )}
    </>
  );
}
